import { useState } from "react";
import { useSearchParams } from "react-router";
import { ShieldAlert, Link2 } from "lucide-react";
import { Tabs, TabPanel } from "@/components/ui/Tabs";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { AssaultIncidentsList } from "@/features/sexual-assault/AssaultIncidentsList";
import { TraffickingCasesList } from "@/features/trafficking/TraffickingCasesList";

// ---------------------------------------------------------------------------
// Tab config
// ---------------------------------------------------------------------------

const tabs = [
  { id: "incidents", label: "Assault Incidents", icon: ShieldAlert },
  { id: "trafficking", label: "Human Trafficking", icon: Link2 },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SexualAssaultPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "incidents";
  const [personId, setPersonId] = useState(searchParams.get("personId") ?? "");
  const [activePersonId, setActivePersonId] = useState(
    searchParams.get("personId") ?? "",
  );

  function handleTabChange(tabId: string) {
    setSearchParams((prev) => {
      prev.set("tab", tabId);
      return prev;
    });
  }

  function handleLookup() {
    if (personId.trim()) {
      setActivePersonId(personId.trim());
      setSearchParams((prev) => {
        prev.set("personId", personId.trim());
        return prev;
      });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          Sexual Assault / Trafficking
        </h1>
        <p className="text-sm text-muted-foreground">
          Track sexual assault incidents and human trafficking cases
        </p>
      </div>

      {/* Client lookup */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-end gap-3">
            <Input
              label="Client ID"
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
              placeholder="Enter client/person ID..."
              className="max-w-xs"
            />
            <Button size="sm" onClick={handleLookup}>
              Load Client
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />

      {activePersonId ? (
        <>
          <TabPanel id="incidents" activeTab={activeTab}>
            <AssaultIncidentsList personId={activePersonId} />
          </TabPanel>

          <TabPanel id="trafficking" activeTab={activeTab}>
            <TraffickingCasesList personId={activePersonId} />
          </TabPanel>
        </>
      ) : (
        <Card>
          <CardContent className="p-8 text-center">
            <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              Enter a client ID above to view their assault incidents and
              trafficking cases.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
